import { useState } from "react";

import { Box, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate, useBlocker, generatePath } from "react-router";

import { AssetCountEventType, AssetCountStatus } from "analytics/events";
import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useHubSlug } from "shared/hooks/useHubSlug";
import { useScan } from "shared/hooks/useScan/useScan";
import { Button } from "ui/Button/Button";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import {
  BatteryCountScan,
  BatteryTasksLoading,
  BatteriesCountedList,
  DiscardBatteryCountModal,
  AddNewBatteryModal,
  ConfirmMissingBatteriesModal,
} from "../components";
import { useBarcodeInformation } from "../hooks/useBarcodeInformation";
import { useGetAssetsByHubLazyQuery, useSearchAssetsLazyQuery } from "../queries/assets.generated";
import { useFinishRecurrentTaskMutation } from "../queries/tasks.generated";
import { useAssetManagementStore } from "../stores/assetManagementStore";
import {
  ASSET_TYPE,
  FULL_LIST_ASSET_STATES,
  FUNCTIONAL_ASSET_STATES,
  NON_FUNCTIONAL_ASSET_STATES,
  VALID_REPORT_STATES,
} from "../utils/constants";
import { serialiseReportedAssets } from "../utils/outboundAssets";

export function BatteryCountScanPage() {
  const intl = useIntl();
  const hubSlug = useHubSlug();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { processBarcode } = useBarcodeInformation();
  const {
    scannedBarcode,
    countedAssets,
    reportedAssets,
    currentTaskId,
    countSessionId,
    setSearchedAsset,
    setScannedBarcode,
    setCountedAssets,
    resetCount,
    setShouldCreateAsset,
    setShouldTransferAsset,
    setShouldUpdateAsset,
    setMatchedSuppliersFromScan,
    setMissingAssets,
    reset: resetStore,
  } = useAssetManagementStore();

  const [nextLocation, setNextLocation] = useState(routes.inbound.root);
  const [highlightedBatteryId, setHighlightedBatteryId] = useState<string | null>(null);
  const [expectedCount, setExpectedCount] = useState(0);

  const [searchAssets, { loading: isSearchingAssets }] = useSearchAssetsLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      showToastUI({
        status: "error",
        description: error.message,
      });
    },
  });
  const [getAssetsByHub, { loading: isGettingHubAssets }] = useGetAssetsByHubLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      showToastUI({
        status: "error",
        description: error.message,
      });
    },
  });
  const [finishRecurrentTask, { loading: isFinishingTask }] = useFinishRecurrentTaskMutation({
    fetchPolicy: "network-only",
    onError: (error) => {
      showToastUI({
        status: "error",
        description: error.message,
      });
    },
  });

  const {
    isOpen: isOpenAddNewBatteryModal,
    onOpen: openAddNewBatteryModal,
    onClose: closeAddNewBatteryModal,
  } = useDisclosure();

  const {
    isOpen: isOpenConfirmMissingModal,
    onOpen: openConfirmMissingModal,
    onClose: closeConfirmMissingModal,
  } = useDisclosure();

  const batteryCount = countedAssets.length;
  const title = intl.formatMessage({ id: "pages.inventory.battery-count-page.title" });
  const displayTitle = batteryCount > 0 ? `${title} (${batteryCount})` : title;

  const functionalBatteries = countedAssets.filter((battery) =>
    FUNCTIONAL_ASSET_STATES.includes(battery.state),
  );
  const nonFunctionalBatteries = countedAssets.filter((battery) =>
    NON_FUNCTIONAL_ASSET_STATES.includes(battery.state),
  );

  const blocker = useBlocker((args: any) => {
    if (batteryCount > 0 && args.nextLocation.pathname !== args.currentLocation.pathname) {
      setNextLocation(args.nextLocation.pathname);
      const isAllowedPath =
        (args.nextLocation.pathname === routes.inventory.batteryCount.root &&
          args.nextLocation.state?.isTaskFinished) ||
        args.nextLocation.pathname === routes.inventory.batteryCount.missing ||
        (isNotNullNorUndefined(scannedBarcode) &&
          args.nextLocation.pathname ===
            generatePath(routes.inventory.batteryCount.create, { serialNumber: scannedBarcode }));
      const shouldBlock = !isAllowedPath;
      return shouldBlock;
    }
    return false;
  });

  const handleDiscard = () => {
    if (reportedAssets.length > 0) {
      sendSegmentTrackEvent("assetOutboundConfirmed", {
        asset_type: ASSET_TYPE,
        asset_count_process_id: currentTaskId!,
        asset_count_session_id: countSessionId!,
        number_of_outbounded_assets: reportedAssets.length,
        assets: serialiseReportedAssets(reportedAssets),
      });
    }
    resetCount();
    setHighlightedBatteryId(null);
    blocker?.proceed?.();
    navigate(nextLocation);
  };

  const handleNotDiscard = () => {
    blocker?.reset?.();
  };

  const goToCreateAssetPage = () => {
    const barcode = scannedBarcode;
    closeAddNewBatteryModal();
    if (isNotNullNorUndefined(barcode)) {
      navigate(
        generatePath(routes.inventory.batteryCount.create, {
          serialNumber: barcode,
        }),
        {
          state: { from: routes.inventory.batteryCount.scan },
        },
      );
    }
  };

  const handleConfirmCount = async () => {
    const { data: assetsByHubData } = await getAssetsByHub({
      variables: {
        input: {
          state: FULL_LIST_ASSET_STATES,
          assetType: ASSET_TYPE,
        },
      },
      fetchPolicy: "network-only",
    });

    const hubAssets = assetsByHubData?.getAssetsByHub?.assets ?? [];
    const countedSerialNumbers = new Set(countedAssets.map((a) => a.serialNumber));
    const missing = hubAssets.filter((asset) => !countedSerialNumbers.has(asset.serialNumber));

    if (missing.length > 0) {
      setMissingAssets(missing);
      setExpectedCount(hubAssets.length);
      openConfirmMissingModal();
    } else {
      try {
        await finishRecurrentTask({
          variables: {
            input: {
              count: countedAssets.length,
              recurrentTaskId: currentTaskId!,
            },
          },
        });
        sendSegmentTrackEvent("assetCountStateUpdated", {
          asset_event_type: AssetCountEventType.AssetCountFinished,
          asset_count_process_id: currentTaskId!,
          asset_count_session_id: countSessionId!,
          status: AssetCountStatus.Done,
          asset_type: ASSET_TYPE,
          number_of_asset_counted: countedAssets.length,
        });

        if (reportedAssets.length > 0) {
          sendSegmentTrackEvent("assetOutboundConfirmed", {
            asset_type: ASSET_TYPE,
            asset_count_process_id: currentTaskId!,
            asset_count_session_id: countSessionId!,
            number_of_outbounded_assets: reportedAssets.length,
            assets: serialiseReportedAssets(reportedAssets),
          });
        }
        resetStore();
        navigate(routes.inventory.batteryCount.root, { state: { isTaskFinished: true } });
        showToastUI({
          id: "finish_task_success",
          title: "pages.inventory.battery-scan-page.finish-task-success",
          variant: "success",
        });
      } catch {
        showToastUI({
          id: "finish_task_error",
          title: "pages.inventory.battery-scan-page.finish-task-error",
        });
      }
    }
  };

  const handleConfirmMissing = () => {
    sendSegmentTrackEvent("assetMissingConfirmed", {
      asset_type: ASSET_TYPE,
      asset_count_process_id: currentTaskId!,
      asset_count_session_id: countSessionId!,
      expected_asset_count: expectedCount,
      scanned_asset_count: countedAssets.length,
    });
    closeConfirmMissingModal();
    navigate(routes.inventory.batteryCount.missing);
  };

  const handleScan = async (barcode: string) => {
    const { isValid, supplierName, matchedSuppliers, serialNumber } = processBarcode(barcode);
    if (!isValid) {
      showToastUI({
        id: "invalid_barcode_format",
        title: "pages.inventory.battery-scan-page.invalid-barcode-format",
      });
      return;
    }

    if (countedAssets.some((battery) => battery.serialNumber === serialNumber)) {
      showToastUI({
        variant: "success",
        id: "battery_already_counted",
        title: "pages.inventory.battery-scan-page.battery-already-counted",
      });
      setHighlightedBatteryId(serialNumber);
      setTimeout(() => {
        setHighlightedBatteryId(null);
      }, 4000);
      return;
    }

    // Valid barcode and not duplicated → call backend
    setScannedBarcode(serialNumber);
    setMatchedSuppliersFromScan(matchedSuppliers);

    const input = {
      assetType: ASSET_TYPE,
      serialNumber,
      hubCode: hubSlug,
      supplierId: supplierName,
    };
    const { data, error: errorSearchingAssets } = await searchAssets({ variables: { input } });
    const foundAssets = data?.searchAssets?.assets || [];

    const searchedAsset = foundAssets.find((asset) => asset.serialNumber === serialNumber);

    const isSameHub = searchedAsset?.hubCode === hubSlug;
    const isOperationalOrDefect =
      searchedAsset && VALID_REPORT_STATES.includes(searchedAsset.state);

    setSearchedAsset(searchedAsset || null);

    if (searchedAsset) {
      if (isSameHub && isOperationalOrDefect) {
        setCountedAssets([...foundAssets, ...countedAssets]);
      } else if (isSameHub && !isOperationalOrDefect) {
        setShouldUpdateAsset(true);
        openAddNewBatteryModal();
      } else if (!isSameHub) {
        setShouldTransferAsset(true);
        openAddNewBatteryModal();
      }
    } else if (!searchedAsset && !errorSearchingAssets) {
      setShouldCreateAsset(true);
      openAddNewBatteryModal();
    }
  };

  useScan({
    onScan: handleScan,
  });

  return (
    <Page isCentered isFull isBgGrey justifyContent="flex-start">
      <NavigationHeader
        title={displayTitle}
        onClickGoBack={() => navigate(routes.inventory.batteryCount.root)}
      />

      <Box position="relative" w="full" h="full" bg="grey.150" overflow="auto">
        {(isSearchingAssets || isGettingHubAssets || isFinishingTask) && <BatteryTasksLoading />}
        {batteryCount === 0 && <BatteryCountScan />}
        {batteryCount > 0 && (
          <BatteriesCountedList
            functionalList={functionalBatteries}
            nonFunctionalList={nonFunctionalBatteries}
            highlightedBatteryId={highlightedBatteryId}
          />
        )}
      </Box>

      {batteryCount > 0 && (
        <Box bg="white" w="full" p="s200" mt="auto">
          <Button
            variant="primary"
            isFullWidth
            onClick={handleConfirmCount}
            isLoading={isGettingHubAssets || isFinishingTask}
          >
            {intl.formatMessage({
              id: "pages.inventory.battery-scan-page.button.confirm-count",
            })}
          </Button>
        </Box>
      )}
      <DiscardBatteryCountModal
        isOpen={blocker.state === "blocked"}
        onClose={handleNotDiscard}
        onClickPrimary={handleDiscard}
        onClickSecondary={handleNotDiscard}
      />
      <AddNewBatteryModal
        isOpen={isOpenAddNewBatteryModal}
        onClose={closeAddNewBatteryModal}
        onClickPrimary={goToCreateAssetPage}
        onClickSecondary={closeAddNewBatteryModal}
      />
      <ConfirmMissingBatteriesModal
        isOpen={isOpenConfirmMissingModal}
        onClose={closeConfirmMissingModal}
        onClickPrimary={handleConfirmMissing}
        onClickSecondary={closeConfirmMissingModal}
        expectedCount={expectedCount}
        countedCount={batteryCount}
      />
    </Page>
  );
}
